import React, { useMemo } from 'react';

import { defineMessages, useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import { Text } from '@atlaskit/primitives/compiled';

import type { UpdateWorkItemFieldsAction } from '../../../../../common/types';

import type { statusField_updateWorkItemFieldsActionRenderer$key } from './__generated__/statusField_updateWorkItemFieldsActionRenderer.graphql';

const messages = defineMessages({
	action: {
		id: 'update-work-item-fields-action-renderer.ui.action-card.status-field.action',
		defaultMessage: 'Move to {newStatus}',
		description: 'Label for the change action in the status field',
	},
	moveFromOriginalStatus: {
		id: 'update-work-item-fields-action-renderer.ui.action-card.status-field.move-from-original-status',
		defaultMessage: 'Move from {originalStatus} to {newStatus}',
		description: 'Label for the change action in the status field',
	},
});

export type Props = {
	queryRef: statusField_updateWorkItemFieldsActionRenderer$key;
	action: UpdateWorkItemFieldsAction;
};

export const StatusField = ({ queryRef, action }: Props): React.JSX.Element | null => {
	const { formatMessage } = useIntl();

	const data = useFragment(
		graphql`
			fragment statusField_updateWorkItemFieldsActionRenderer on JiraStatusField {
				fieldId
				status {
					name
				}
				transitions @optIn(to: "JiraStatusFieldOptions") {
					edges {
						node {
							transitionId
							to {
								name
							}
						}
					}
				}
			}
		`,
		queryRef,
	);

	const fieldIdMatches = data?.fieldId === action.data.fieldKey;
	const suggestedTransitionId = Number(action?.data?.fieldValue);

	const isValidNumericTransition = !Number.isNaN(suggestedTransitionId);

	const statusName = useMemo(() => {
		if (!fieldIdMatches) {
			return undefined;
		}
		if (!isValidNumericTransition) {
			return typeof action?.data?.fieldValue === 'string'
				? String(action.data.fieldValue).trim()
				: undefined;
		}
		return data?.transitions?.edges?.find(
			(edge) => edge?.node?.transitionId === suggestedTransitionId,
		)?.node?.to?.name;
	}, [
		data,
		fieldIdMatches,
		isValidNumericTransition,
		suggestedTransitionId,
		action?.data?.fieldValue,
	]);

	if (!fieldIdMatches || !statusName) {
		return null;
	}

	return (
		<Text
			testId="update-work-item-fields-action-renderer.ui.action-card.card-fields-list.status-field"
			color="color.text.subtle"
			maxLines={1}
		>
			{data?.status?.name && isValidNumericTransition
				? formatMessage(messages.moveFromOriginalStatus, {
						originalStatus: data?.status?.name,
						newStatus: statusName,
					})
				: formatMessage(messages.action, {
						newStatus: statusName,
					})}
		</Text>
	);
};
